// Proactively detect that a newer build has been deployed to S3 so we can nudge the user to
// refresh BEFORE they hit a stale-chunk error. No build-time version stamp is needed: Vite
// gives the entry module a content hash that changes on every deploy, so we compare the
// hashed entry-script path(s) in the currently-running document against the ones in a freshly
// fetched index.html.
//
// NOTE: relies on index.html being served with `Cache-Control: no-cache` (hashed assets stay
// immutable). That is the standard SPA-on-S3/CloudFront setup; without it the poll can lag.

const collectEntrySignature = (doc: Document): string => {
  const scripts = Array.from(doc.querySelectorAll('script[type="module"][src]')) as HTMLScriptElement[]
  return scripts
    .map((s) => s.getAttribute('src') || '')
    .filter((src) => src.includes('/assets/'))
    .map((src) => {
      try {
        return new URL(src, window.location.origin).pathname
      } catch {
        return src
      }
    })
    .sort()
    .join('|')
}

// Captured once, at module load, from the build that is actually running.
const CURRENT_SIGNATURE = collectEntrySignature(document)

export const hasNewVersion = async (): Promise<boolean> => {
  // Bypass the browser cache so we see what S3/CloudFront is actually serving right now.
  const res = await fetch(`${window.location.origin}/index.html`, { cache: 'no-store' })
  if (!res.ok) return false
  const html = await res.text()
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const latest = collectEntrySignature(doc)
  // If either signature is empty we couldn't read the entry scripts — don't claim a new
  // version, to avoid false nudges (e.g. an error page or unexpected HTML shape).
  if (!CURRENT_SIGNATURE || !latest) return false
  return latest !== CURRENT_SIGNATURE
}
